// main.ts — bootstrap.
//
// The listener arrives via a session link. We resolve the session id from the
// URL (in priority order):
//   1. ?s=<sessionId>            (query param)
//   2. /s/<sessionId>            (path segment, when the server routes it here)
//   3. #<sessionId>              (hash, e.g. /#abc123)
// then hand off to the App state machine (ui.ts).

import { App } from "./ui";
import { HostDashboard } from "./host";
import { resolveSessionId } from "./session-url";

/** True for the host dashboard route: /host (any trailing slash) or #host. */
function isHostRoute(): boolean {
  const path = window.location.pathname.replace(/\/+$/, "");
  return path === "/host" || window.location.hash.replace(/^#\/?/, "") === "host";
}

function renderNoSession(root: HTMLElement): void {
  root.dataset.state = "error";
  root.innerHTML = `
    <main class="card card--center">
      <h1 class="card__title">No session link</h1>
      <p class="card__sub">
        This link does not contain a session. Open the full link the host sent
        you (e.g. <code>…/?s=YOUR-SESSION</code>).
      </p>
    </main>
  `;
}

function boot(): void {
  const root = document.getElementById("app");
  if (!root) {
    // index.html guarantees #app; this is just a hard guard.
    document.body.textContent = "Error: #app container missing.";
    return;
  }

  // Host dashboard route (browser login + session history + plugin token).
  if (isHostRoute()) {
    void new HostDashboard(root).start();
    return;
  }

  const sessionId = resolveSessionId();
  if (!sessionId) {
    renderNoSession(root);
    return;
  }

  const app = new App({ root, sessionId });
  void app.start();

  // Best-effort cleanup so we leave the LiveKit room on navigation away.
  // NOT {once:true}: Safari's bfcache restores the page ALIVE on back-nav —
  // pageshow(persisted) rejoins cleanly, and the next pagehide must still fire.
  window.addEventListener("pagehide", () => void app.destroy());
  window.addEventListener("pageshow", (e) => {
    if ((e as PageTransitionEvent).persisted) void app.start();
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", boot, { once: true });
} else {
  boot();
}
